import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Edit2, Save, X, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { formatDateIST } from "@/utils/dateUtils";
import {
  TIME_RANGES, getMinAppointmentDate, getMaxAppointmentDate, isDelhiveryAppointmentBooked,
  isValidAppointmentDate,
  validateReferenceRequiredForAppointment, validatePoExpiryAndAppointment, validateOrderPoExpiry,
} from "@/utils/appointmentUtils";

/**
 * Inline editors for the Order Information card, shared by the seller + admin Order Detail pages.
 * Both the Appointment and the Reference/PO editors are LOCKED (hidden) once the Delhivery
 * appointment is booked — the appointment + PO numbers have gone to the carrier by then, so a
 * later change would desync (reschedule from the Appointments page instead).
 *
 * They enforce the same rule cluster as create (utils/po_validation.py mirror): an appointment
 * requires a reference/PO number; a reference requires its date + the order PO expiry; and the
 * appointment must be on/before the PO expiry.
 */

// Completed/terminal statuses: never edit an appointment or reference on these (editing a
// delivered order's appointment would trigger a pointless/risky carrier reschedule).
const FINAL_STATUSES = ["DELIVERED", "RETURN_DELIVERED", "LOST", "RETURN_OFD", "CANCELLED"];

/** Editors are locked once the appointment is booked with the carrier OR the order is terminal. */
const editorsLocked = (order) => isDelhiveryAppointmentBooked(order) || FINAL_STATUSES.includes(order?.status);

/** Format a Date's LOCAL calendar day as 'YYYY-MM-DD' (toISOString would shift a day in IST). */
const toLocalYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Coerce any date/ISO value to a 'YYYY-MM-DD' string for an <input type="date">. */
const toDateInput = (d) => {
  if (!d) return "";
  const s = String(d);
  // Already 'YYYY-MM-DD...' — slice; else format the parsed Date's LOCAL day.
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const parsed = new Date(s);
  return isNaN(parsed) ? "" : toLocalYMD(parsed);
};

/** Editable "Appointment Date & Time" row. */
export function AppointmentEditRow({ order, onSaved }) {
  const locked = editorsLocked(order);
  const booked = isDelhiveryAppointmentBooked(order);
  const [editing, setEditing] = useState(false);
  // Only pre-fill the date if it's still bookable (tomorrow..+15); a stale/past value would 400
  // on save. A breached/past appointment starts blank so the user must pick a fresh date.
  const [date, setDate] = useState(isValidAppointmentDate(order?.appointment_date) ? order.appointment_date : "");
  const [timeRange, setTimeRange] = useState(order?.appointment_time_range || "");
  const [appointmentId, setAppointmentId] = useState(order?.delhivery_appointment_id || order?.appointment_id || "");
  const [saving, setSaving] = useState(false);

  const currentLabel = order?.appointment_date
    ? `${order.appointment_date}${order.appointment_time_range ? ` · ${order.appointment_time_range}` : ""}`
    : (order?.appointment_datetime ? formatDateIST(order.appointment_datetime, "short") : "N/A");

  const minD = toDateInput(getMinAppointmentDate());
  const maxD = toDateInput(getMaxAppointmentDate());

  const startEdit = () => {
    setDate(isValidAppointmentDate(order?.appointment_date) ? order.appointment_date : "");
    setTimeRange(order?.appointment_time_range || "");
    setAppointmentId(order?.delhivery_appointment_id || order?.appointment_id || "");
    setEditing(true);
  };

  const save = async () => {
    if (!date || !timeRange) { toast.error("Select an appointment date and time range"); return; }
    if (!isValidAppointmentDate(date)) { toast.error("Pick an appointment date from tomorrow up to 15 days out"); return; }
    // appointment => reference => (date + PO expiry). The reference lives on the order; if it's
    // missing, the appointment can't be set here — point the user at the Reference editor.
    const refErr = validateReferenceRequiredForAppointment(order?.reference_numbers, date);
    if (refErr) { toast.error(`${refErr} Add it in "Reference Numbers" first.`); return; }
    const poErr = validateOrderPoExpiry(order?.reference_numbers, toDateInput(order?.po_expiry_date), date);
    if (poErr) { toast.error(poErr); return; }
    if (order?.marketplace_appointment_id_required && !appointmentId.trim()) {
      toast.error("Appointment ID is required to book an appointment for this marketplace"); return;
    }
    setSaving(true);
    try {
      const resp = await axios.put(`${API}/appointments/orders/${order.id}/appointment`, {
        appointment_date: date, appointment_time_range: timeRange, appointment_id: appointmentId.trim(),
      });
      toast.success("Appointment updated");
      const patch = {
        appointment_date: date, appointment_time_range: timeRange,
        appointment_datetime: `${date}T12:00:00`,
        appointment_id: appointmentId.trim(),
      };
      // If the carrier booking succeeded on this save, the row locks from now on.
      if (resp.data?.delhivery_booking?.success) patch.delhivery_appointment_booked = true;
      onSaved?.(patch);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update appointment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Appointment Date &amp; Time</p>
        {!locked && !editing && (
          <Button variant="ghost" size="sm" onClick={startEdit} className="h-6 text-xs">
            <Edit2 className="w-3 h-3 mr-1" />{order?.appointment_date ? "Edit" : "Add"}
          </Button>
        )}
        {!locked && editing && (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={save} disabled={saving} className="h-6 text-green-600">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)} className="h-6 text-slate-500">
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-1 space-y-2">
          <Input type="date" value={date} min={minD} max={maxD}
            onChange={(e) => setDate(e.target.value)} className="h-9 max-w-[200px]" />
          <Select value={timeRange} onValueChange={setTimeRange} disabled={!date}>
            <SelectTrigger className="h-9 max-w-[220px]"><SelectValue placeholder="Time range" /></SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div>
            <Input value={appointmentId} onChange={(e) => setAppointmentId(e.target.value)}
              placeholder={`Delhivery Appointment ID${order?.marketplace_appointment_id_required ? " *" : " (optional)"}`}
              className="h-9 max-w-[220px]" />
            {order?.marketplace_appointment_id_required &&
              <p className="text-[11px] text-red-500 mt-0.5">Required to book the appointment for this marketplace.</p>}
          </div>
        </div>
      ) : (
        <p className="font-semibold text-slate-900">
          {currentLabel}
          {booked && <span className="ml-2 text-xs font-medium text-emerald-600">Booked ✓</span>}
        </p>
      )}
    </div>
  );
}

/** Editable "Reference Numbers" row — one dialog for the reference number(s), their dates, and
 *  the order-level PO expiry (the whole validation cluster). */
export function ReferenceEditRow({ order, onSaved }) {
  const locked = editorsLocked(order);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([{ reference_number: "", date: "" }]);
  const [poExpiry, setPoExpiry] = useState("");
  const [saving, setSaving] = useState(false);

  const refs = order?.reference_numbers || [];
  const hasAny = refs.length > 0 || order?.reference_number;

  const openDialog = () => {
    const existing = refs.length > 0
      ? refs.map((r) => ({ reference_number: r.reference_number || "", date: r.date || "" }))
      : (order?.reference_number
          ? [{ reference_number: order.reference_number, date: order.reference_number_date || "" }]
          : [{ reference_number: "", date: "" }]);
    setRows(existing);
    setPoExpiry(toDateInput(order?.po_expiry_date));
    setOpen(true);
  };
  const setRow = (i, k, v) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((p) => [...p, { reference_number: "", date: "" }]);
  const removeRow = (i) => setRows((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p));

  const save = async () => {
    const clean = rows
      .map((r) => ({ reference_number: (r.reference_number || "").trim(), date: (r.date || "").trim() }))
      .filter((r) => r.reference_number);
    const dErr = validatePoExpiryAndAppointment(clean, order?.appointment_date);   // ref => its date
    if (dErr) { toast.error(dErr); return; }
    const poErr = validateOrderPoExpiry(clean, poExpiry, order?.appointment_date);  // ref => PO expiry, appt <= expiry
    if (poErr) { toast.error(poErr); return; }
    const refReq = validateReferenceRequiredForAppointment(clean, order?.appointment_date);  // has-appt => ref stays
    if (refReq) { toast.error(refReq); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/orders/${order.id}/reference-numbers`, {
        reference_numbers: clean, po_expiry_date: poExpiry || null,
      });
      toast.success("Reference numbers updated");
      onSaved?.({
        reference_numbers: clean,
        reference_number: clean[0]?.reference_number || null,
        reference_number_date: clean[0]?.date || null,
        po_expiry_date: poExpiry || null,
      });
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update reference numbers");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Reference Numbers</p>
        {!locked && (
          <Button variant="ghost" size="sm" onClick={openDialog} className="h-6 text-xs">
            <Edit2 className="w-3 h-3 mr-1" />{hasAny ? "Edit" : "Add"}
          </Button>
        )}
      </div>
      {refs.length > 0 ? (
        <div className="space-y-1">
          {refs.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="font-semibold text-slate-900">{r.reference_number}</span>
              {r.date && <span className="text-sm text-slate-500">({r.date})</span>}
            </div>
          ))}
        </div>
      ) : order?.reference_number ? (
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900">{order.reference_number}</span>
          {order.reference_number_date && <span className="text-sm text-slate-500">({order.reference_number_date})</span>}
        </div>
      ) : (
        <p className="font-semibold text-slate-900">N/A</p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Reference / PO Numbers</DialogTitle>
            <DialogDescription>Order {order?.order_number || order?.id}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {order?.appointment_date && (
              <p className="text-xs text-red-500">
                This order has an appointment on {order.appointment_date} — a reference/PO number with its date and PO expiry is required.
              </p>
            )}
            {rows.map((r, i) => (
              <div key={i} className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-slate-500">Reference Number</Label>
                  <Input value={r.reference_number} placeholder="PO/Reference number"
                    onChange={(e) => setRow(i, "reference_number", e.target.value)} />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-slate-500">Date{r.reference_number ? " *" : ""}</Label>
                  <Input type="date" value={r.date} onChange={(e) => setRow(i, "date", e.target.value)} />
                </div>
                {rows.length > 1 && (
                  <Button type="button" variant="ghost" size="icon"
                    onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              <Plus className="w-4 h-4 mr-1" />Add Reference
            </Button>
            <div className="space-y-1 max-w-xs">
              <Label className="text-xs text-slate-500">
                PO Expiry Date{rows.some((r) => (r.reference_number || "").trim()) ? " *" : ""}
              </Label>
              <Input type="date" value={poExpiry} onChange={(e) => setPoExpiry(e.target.value)} />
              <p className="text-[11px] text-slate-400">Required when a reference/PO number is entered.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
